import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Image,
  Modal,
  Row,
  Spinner,
  Stack,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faFile,
  faFileExcel,
  faFilePdf,
  faFileWord,
} from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios, { isAxiosError } from 'axios';
import { groupBy } from 'lodash';
import Sentry from 'src/services/sentry';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import CustomModal from 'src/components/custom_components/CustomModal/CustomModal';

import { About } from 'src/models/AboutTab.model';
import { Api } from 'src/models/Api.model';
import { Download, whichDownload } from 'src/models/Downloads.model';
import { DeviceDataValue } from 'src/models/GalenData/DeviceDataValue.model';
import { GalenErrorKeys } from 'src/models/GalenData/GalenError.model';
import { I18NextNS } from 'src/models/Language.model';
import { Product, ProductFullName } from 'src/models/Product.model';

import useGetCurrentUser from 'src/hooks/useGetCurrentUser';
import useGetGalenErrorMsg from 'src/hooks/useGetGalenErrorMsg';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';

function imgSrc(type: string | undefined) {
  return new URL(`/src/assets/img/AboutTab/${type}.png`, import.meta.url).href;
}

const AboutTab = () => {
  const [showOss, setShowOss] = useState(false);
  const [ossText, setOssText] = useState('');
  const [pageData, setPageData] = useState<PageData>();
  const [manuals, setManuals] = useState<DeviceDataValue<string>[]>([]);

  // TODO: to be uncommented when TOS is implemented
  // const [termsOfServices, setTermsOfServices] = useState<
  //   DeviceDataValue<string>[]
  // >([]);

  const [otherFiles, setOtherFiles] = useState<DeviceDataValue<string>[]>([]);

  const handleCloseOss = () => setShowOss(false);
  const handleShowOss = () => {
    const OSS_URL = '/THIRD-PARTY-NOTICES.txt';
    if (ossText === '') {
      axios
        .get(OSS_URL)
        .then((res) => {
          setOssText(res.data);
          setShowOss(true);
        })
        .catch((err) => {
          setOssText(`Error: ${err.message}`);
          setShowOss(true);
        });
    } else {
      setShowOss(true);
    }
  };

  interface IfileIcons {
    pdf: IconProp;
    doc: IconProp;
    xls: IconProp;
    [key: string]: IconProp;
  }

  const fileIcons: IfileIcons = {
    pdf: faFilePdf,
    doc: faFileWord,
    xls: faFileExcel,
  };

  interface PageData {
    ManufacturerText?: DeviceDataValue<string>;
    Email?: DeviceDataValue<string>;
    Name?: DeviceDataValue<string>;
    PhoneNumber?: DeviceDataValue<string>;
  }

  const DownloadItem = (download: DeviceDataValue<string>) => (
    <li>
      <FontAwesomeIcon
        icon={fileIcons[download?.value.split('.').pop() || ''] || faFile}
        className="me-3 text-dark"
      />
      <a
        href={download?.link}
        rel="noreferrer"
        className="text-dark"
        data-track="support.download-user-manual"
        data-test="download"
      >
        {download?.value}
      </a>
    </li>
  );

  const [isLoading, setIsLoading] = useState(false);

  const {
    practiceSettings: { product },
  } = useRemoteBoundStore();

  const { practiceId, userEmail } = useGetCurrentUser();

  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.PAGES);

  const { getErrorMessage } = useGetGalenErrorMsg();
  const { showHttpRequestError } = useHandleHttpRequestError();

  useEffect(() => {
    const fetchText = async () => {
      try {
        const data = await Api.remote._get_all_device_data({
          deviceDataModelId: import.meta.env.VITE_ABOUT_PAGE_DATA_MODEL,
          pageNumber: 0,
          pageSize: 1,
        });

        const deviceDataList = data.data.content || [];
        return deviceDataList[0]['data'];
      } catch (error) {
        if (
          isAxiosError(error) &&
          error.response &&
          error.response.status === 404
        ) {
          Sentry.captureMessage(
            `Missing contact support data in About page for user ${userEmail} and practice ${practiceId}`,
          );

          return Api.alertBox({
            title: t('bootbox.title.Error', {
              ns: I18NextNS.COMMON,
            }),
            message: t('about.missingContactSupport'),
            locale: language,
            type: 'error',
          });
        }
        showHttpRequestError(error);
      }
    };

    const fetchRegion = async () => {
      try {
        if (!practiceId) {
          throw new Error(getErrorMessage(GalenErrorKeys.PRACTICE_IS_REQUIRED));
        }

        const practice = await Api.remote.get_practice(practiceId);
        const country = practice.data.contactInfo.country;

        return country;
      } catch (error) {
        showHttpRequestError(error);
      }
    };

    const fetchFiles = async () => {
      try {
        const data = await Api.remote._get_all_device_data({
          deviceDataModelId: import.meta.env
            .VITE_ABOUT_PAGE_DOWNLOAD_DATA_MODEL,
        });
        const deviceDataList = data.data.content || [];
        return deviceDataList.map((item) => item.data.File);
      } catch (error) {
        if (
          isAxiosError(error) &&
          error.response &&
          error.response.status === 404
        ) {
          return [];
        }

        showHttpRequestError(error);
      }
    };

    const processFilename = (item: DeviceDataValue<string>) => {
      const fileSplit = item.value.split(' ');
      const fileType = whichDownload(fileSplit[0]);
      const fileRegion = fileSplit[1];

      return { ...item, fileType, fileRegion };
    };

    const fetchPage = async () => {
      try {
        const textData = await fetchText();
        if (textData !== undefined) {
          setPageData(textData);
        }

        const regionData = await fetchRegion();

        if (regionData !== undefined) {
          const filesData = await fetchFiles();
          if (filesData !== undefined) {
            const processedFileData = filesData.map(processFilename);

            const regionList = processedFileData.filter(
              (item) => item.fileRegion === regionData,
            );

            const groupedFiles = groupBy(regionList, 'fileType');

            const tempManuals = groupedFiles[Download.userManual] || [];

            const tempOtherFiles = groupedFiles[Download.other] || [];

            setManuals(tempManuals);

            setOtherFiles(tempOtherFiles);
          }
        }
      } catch (error) {
        showHttpRequestError(error);
      }
    };

    setIsLoading(true);
    fetchPage().then(() => setIsLoading(false));
  }, [
    showHttpRequestError,
    language,
    practiceId,
    t,
    userEmail,
    getErrorMessage,
  ]);

  return (
    <>
      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center h-100">
          <Spinner className="mx-5 my-5" variant="primary" />
        </div>
      ) : (
        <div className="overflow-y-auto overflow-x-hidden">
          <Row>
            <Col>
              <h3 data-test="about-page-title">{t('about.title')}</h3>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Card className="data-container mt-2 p-0">
                <Card.Body>
                  <Stack gap={2}>
                    <Stack direction="horizontal" gap={3}>
                      <Image
                        src={imgSrc(About.ManufacturerSymbol)}
                        alt={t('about.manufacturerSymbolImgAlt')}
                        fluid
                        style={{ width: 50 }}
                        data-test="product-symbols"
                      />

                      <div
                        className="d-flex flex-column"
                        data-test="company-info"
                      >
                        <span className="fw-semibold">
                          {t('about.manufacturerName', {
                            company: About.ManufacturerName,
                            parent: About.ManufacturerParentName,
                          })}
                        </span>
                        <span>
                          {`${t('about.manufacturerBuilding')}, ${About.ManufacturerAddress}, ${t('about.manufacturerCounty')}`}
                        </span>
                      </div>
                    </Stack>
                  </Stack>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="data-container mt-2 p-0">
                <Card.Body>
                  <h5>{t('about.productVersion')}</h5>
                  <p
                    className="pb-1"
                    data-test="product-version"
                  >{`${ProductFullName(product)} ${About.ProductVersion}`}</p>

                  <h5 className="pt-3 border-top" data-test="contact-support">
                    {t('about.contactSupport')}
                  </h5>
                  <ul className="list-unstyled">
                    <li data-test="contact-name">{pageData?.Name?.value}</li>
                    <li>
                      <a
                        href={`mailto:${pageData?.Email?.value}`}
                        rel="noreferrer"
                        className="text-dark"
                        data-test="contact-email"
                        data-track="support.email"
                      >
                        {pageData?.Email?.value}
                      </a>
                    </li>
                    <li data-test="contact-phone">
                      {pageData?.PhoneNumber?.value}
                    </li>
                  </ul>
                  {pageData?.ManufacturerText?.value && (
                    <p
                      className="pt-3 pb-1 border-top"
                      data-test="manufacturer-text"
                    >
                      {pageData?.ManufacturerText?.value || null}
                    </p>
                  )}
                  {
                    <Stack gap={3} data-test="user-manuals">
                      <div className="pt-3 border-top d-flex align-items-center">
                        <Image
                          src={imgSrc(About.UserManualSymbol)}
                          alt=""
                          fluid
                          className="me-3"
                          style={{ height: 40 }}
                        />
                        <h5 className="pt-2">{t('about.userManuals')}</h5>
                      </div>

                      <ul className="list-unstyled">
                        {manuals
                          .filter(
                            (item) =>
                              product === Product.TELEOPHTH_STANDALONE ||
                              !item.value.includes(
                                ProductFullName(Product.TELEOPHTH_STANDALONE),
                              ),
                          )
                          .map((manual) => (
                            <DownloadItem key={manual.reference} {...manual} />
                          ))}
                      </ul>
                    </Stack>
                  }

                  {/* TODO: to be uncommented when TOS is implemented */}
                  {/* <h3 className="pt-3 border-top">
                    {t('about.termsOfService')}
                  </h3>
                  <ul className="list-unstyled">
                    {termsOfServices.map((termsOfService) => (
                      <DownloadItem
                        key={termsOfService.reference}
                        {...termsOfService}
                      />
                    ))}
                  </ul> */}

                  {otherFiles && (
                    <Stack gap={3} data-test="other-downloads">
                      <h5 className="pt-3 border-top">
                        {t('about.otherDownloads')}
                      </h5>
                      <ul className="list-unstyled">
                        {otherFiles
                          .filter(
                            (item) =>
                              product === Product.TELEOPHTH_STANDALONE ||
                              !item.value.includes(
                                ProductFullName(Product.TELEOPHTH_STANDALONE),
                              ),
                          )
                          .map((otherFile) => (
                            <DownloadItem
                              key={otherFile.reference}
                              {...otherFile}
                            />
                          ))}
                      </ul>
                    </Stack>
                  )}
                  <p className="pt-3 border-top">
                    {t('about.productRegistered', {
                      product: ProductFullName(product),
                    })}
                  </p>
                  <p data-test="company-copyright">
                    <strong>
                      &copy;
                      {t('about.copyright', {
                        year: new Date().getFullYear(),
                      })}
                    </strong>

                    <a
                      data-test="third-party-notices"
                      href="#/about"
                      className="ms-2 text-black"
                      onClick={handleShowOss}
                    >
                      {t('about.thirdPartyNotices')}
                    </a>
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <CustomModal
            show={showOss}
            onHide={handleCloseOss}
            centered
            size="xl"
            scrollable={true}
            fullscreen="xl-down"
          >
            <Modal.Header closeButton>
              <Modal.Title>{t('about.thirdPartyNotices')}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0">
              <pre className="m-0 p-3" style={{ maxHeight: '75vh' }}>
                {ossText}
              </pre>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="primary"
                onClick={handleCloseOss}
                data-track="settings.about-oss-modal.close"
                data-observe
              >
                {t('about.close')}
              </Button>
            </Modal.Footer>
          </CustomModal>
        </div>
      )}
    </>
  );
};

export default AboutTab;
